import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { hostLogin, apiError } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { BrandMark } from "../components/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function HostLogin() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!username.trim() || !password.trim())
      return toast.error(t("pleaseEnterCredentials"));
    setLoading(true);
    try {
      const auth = { username: username.trim(), password };
      await hostLogin(auth);
      login(auth);
      navigate("/host/dashboard");
    } catch (e) {
      toast.error(apiError(e) === "Invalid credentials" ? t("invalidCredentials") : t("invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#26422A]" data-testid="host-login-page">
      <div className="lf-grain min-h-screen">
        <header className="mx-auto flex max-w-md items-center justify-between px-5 py-6">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold text-[#FFEEBC] hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" /> {t("back")}
          </button>
          <LanguageSwitcher />
        </header>

        <main className="mx-auto flex max-w-md flex-col items-center px-5 pt-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full rounded-3xl bg-[#FFFFFF] p-8 shadow-2xl"
          >
            <div className="mb-6 flex justify-center">
              <BrandMark size="lg" />
            </div>
            <h1 className="text-center font-display text-3xl font-black text-[#26422A]">
              {t("hostLogin")}
            </h1>
            <p className="mt-2 flex items-center justify-center gap-1.5 text-sm font-medium text-[#26422A]">
              <ShieldCheck className="h-4 w-4" /> {t("staffAccessOnly")}
            </p>

            <div className="mt-8 space-y-4">
              <div className="space-y-2">
                <Label className="font-bold text-[#26422A]">{t("username")}</Label>
                <Input
                  data-testid="host-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t("enterUsername")}
                  className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold text-[#26422A]">{t("password")}</Label>
                <Input
                  data-testid="host-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  placeholder={t("enterPassword")}
                  className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
                />
              </div>
            </div>

            <Button
              data-testid="host-login-btn"
              onClick={handleLogin}
              disabled={loading}
              className="mt-8 h-14 w-full rounded-xl bg-[#26422A] text-base font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("login")}
            </Button>
            <p className="mt-4 text-center text-xs text-[#26422A]">{t("contactManagement")}</p>

            <div className="mt-5 border-t border-[#26422A]/15 pt-4 text-center">
              <button
                type="button"
                data-testid="admin-login-link"
                onClick={() => navigate("/admin/login")}
                className="text-sm font-bold text-[#EA672D] underline underline-offset-2 hover:text-[#d0561f]"
              >
                {t("adminLogin")}
              </button>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
